// Client active/inactive helpers shared by the client and project detail
// pages. A client is inactive once nobody works for it any more; the API
// refuses to deactivate one that still has active projects unless asked to
// close them too (PUT /clients/:id → 409 with `activeProjects`).

// Legacy rows and stale payloads may lack the flag — treat them as active.
export const isClientActive = (client) => client?.active !== false;

export const CLIENT_STATUS_LABEL = { active: 'Active', inactive: 'Inactive' };
export const clientStatus = (client) =>
  isClientActive(client) ? 'active' : 'inactive';

export const activeProjectsOf = (projects, clientId) =>
  (projects || []).filter(
    (project) => project.clientId === clientId && project.status === 'active',
  );

// True when moving `project` to `nextStatus` closes the last active project of
// its client — the moment to ask whether the client should go inactive too.
export function closesLastActiveProject(projects, project, nextStatus) {
  if (project.status !== 'active' || nextStatus === 'active') return false;
  const open = activeProjectsOf(projects, project.clientId);
  return open.length === 1 && open[0].id === project.id;
}

// The full body PUT /clients/:id expects, from a bootstrap client.
export const clientBody = (client, overrides = {}) => ({
  name: client.name,
  tier: client.tier || 'Standard',
  groupName: client.groupName || '',
  contactEmail: client.contactEmail || '',
  active: isClientActive(client),
  ...overrides,
});

export const projectList = (projects) =>
  projects.map((project) => `${project.code} – ${project.name}`).join(', ');

// Saves a client through `put(overrides)`. A 409 listing active projects asks
// `confirm(projects)` whether to close them too; a yes retries with exactly
// those ids, and when the list changed in the meantime the API answers 409
// again with the fresh one, so the question is asked again. Resolves false
// when the user backs out.
export async function saveConfirmingActiveProjects(put, confirm) {
  let overrides = {};
  for (;;) {
    try {
      await put(overrides);
      return true;
    } catch (error) {
      const open = error.status === 409 && error.data?.activeProjects;
      if (!open?.length) throw error;
      if (!(await confirm(open))) return false;
      overrides = { confirmedProjectIds: open.map((project) => project.id) };
    }
  }
}
